"use client";

/**
 * Envio de um report para `/api/bug-reports`.
 *
 * ─── Escrito para correr com a app partida ────────────────────────────────
 *
 * Este é o único caminho da aplicação que se executa DEPOIS de alguma coisa
 * ter rebentado. Isso impõe três regras que explicam o feitio do módulo:
 *
 *   1. Sem dependências. Nada de cliente de API partilhado, nada de contexto
 *      de React, nada de store. Um `fetch` cru. O que estiver partido não pode
 *      estar no caminho de contar que se partiu.
 *
 *   2. Nunca lança. Uma excepção aqui aparecia ao utilizador como um SEGUNDO
 *      erro por cima do primeiro, e a falha de que ele se ia lembrar era a
 *      nossa. Devolve `null` e desiste em silêncio.
 *
 *   3. Com prazo. Se a rede estiver ruim, o ecrã de erro não pode ficar
 *      pendurado à espera — mostra o que tem e segue.
 */
import { getBreadcrumbs } from "@/lib/bug-breadcrumbs";

export interface BugReportResult {
  id: string;
  /** A ocorrência criada. É a ela que as imagens se penduram. */
  eventId: string | null;
  ref: string | null;
  occurrences: number;
  severity: string | null;
}

export interface SubmitBugReportInput {
  kind: "crash" | "manual";
  error?: Error | null;
  /** `componentStack` do React — diz QUE componente rebentou, não só onde. */
  componentStack?: string | null;
  userDescription?: string | null;
}

const TIMEOUT_MS = 8_000;

/**
 * A versão do build que ESTE separador está a correr.
 *
 * Injectada em build. Ver a nota em `resolveAppVersion`, do lado do servidor:
 * um separador aberto há duas horas corre o build anterior, e carimbar o
 * report com a versão do servidor mandava-nos depurar o ficheiro errado.
 */
const APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? null;

export async function submitBugReport(
  input: SubmitBugReportInput,
): Promise<BugReportResult | null> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const payload = {
      kind: input.kind,
      errorName: input.error?.name ?? null,
      errorMessage: input.error?.message ?? null,
      errorStack: input.error?.stack ?? null,
      componentStack: input.componentStack ?? null,
      route:
        typeof window !== "undefined"
          ? window.location.pathname + window.location.search
          : null,
      viewport:
        typeof window !== "undefined"
          ? `${window.innerWidth}x${window.innerHeight}`
          : null,
      appVersion: APP_VERSION,
      userDescription: input.userDescription ?? null,
      // Envolvido em try/catch pela regra 2: o anel é memória simples e não
      // deve conseguir falhar, mas "não deve" não é garantia nenhuma no
      // caminho que corre depois de a app rebentar.
      breadcrumbs: safeBreadcrumbs(),
    };

    const response = await fetch("/api/bug-reports", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
      // A sessão vive em cookie; sem isto o report chega sem utilizador e
      // perde-se a contagem de pessoas afectadas, que é metade da gravidade.
      credentials: "same-origin",
    });

    clearTimeout(timer);

    if (!response.ok) return null;
    return (await response.json()) as BugReportResult;
  } catch {
    // Rede em baixo, prazo esgotado, JSON impossível de escrever — em todos os
    // casos a resposta é a mesma: perde-se o report, não se perde o utilizador.
    return null;
  }
}

/**
 * Sobe as imagens de um relato, depois de ele estar gravado.
 *
 * Uma a uma e em sequência, não em paralelo: o tecto é de quatro, portanto não
 * há nada a ganhar em velocidade, e em série a ordem de chegada é a ordem por
 * que foram coladas — que é a ordem da explicação de quem reportou.
 *
 * Devolve quantas subiram. Regra 2 do módulo: NUNCA lança. Uma imagem que
 * falha é uma imagem a menos num relato que já está gravado — nunca um segundo
 * erro na cara de quem acabou de reportar o primeiro.
 */
export async function uploadBugReportImages(
  reportId: string,
  eventId: string,
  files: File[],
): Promise<number> {
  let subidas = 0;

  for (const file of files) {
    try {
      const form = new FormData();
      form.append("eventId", eventId);
      form.append("file", file);

      const res = await fetch(`/api/bug-reports/${reportId}/attachments`, {
        method: "POST",
        body: form,
        credentials: "same-origin",
      });

      if (res.ok) subidas += 1;
    } catch {
      // Segue para a seguinte: uma imagem perdida não é razão para desistir
      // das outras três.
    }
  }

  return subidas;
}

function safeBreadcrumbs() {
  try {
    return getBreadcrumbs();
  } catch {
    return [];
  }
}
